import type { Source, SyncProgress } from '@viva/core';
import { isVivaError, VivaError } from '@viva/core';
import type { CatalogApi, NewSourceInput } from '@viva/data';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../i18n';

export interface SyncTask {
  label: string;
  progress: SyncProgress | null;
  cancel: () => void;
}

interface CatalogState {
  catalog: CatalogApi;
  sources: Source[] | null;
  /** Error from loading the source list, or from the last sync. */
  error: VivaError | null;
  task: SyncTask | null;
  dismissError: () => void;
  addSource: (input: NewSourceInput) => Promise<boolean>;
  refreshSource: (source: Source) => Promise<boolean>;
  reimportFile: (source: Source, file: File) => Promise<boolean>;
  removeSource: (source: Source) => Promise<void>;
}

const CatalogContext = createContext<CatalogState | null>(null);

export function useCatalog(): CatalogState {
  const value = useContext(CatalogContext);
  if (!value) throw new Error('useCatalog outside CatalogProvider');
  return value;
}

function asVivaError(error: unknown): VivaError {
  return isVivaError(error) ? error : new VivaError('storage', error instanceof Error ? error.message : String(error));
}

/** Owns the source list and the single running sync, across screens. */
export function CatalogProvider({ catalog, children }: { catalog: CatalogApi; children: ReactNode }) {
  const [sources, setSources] = useState<Source[] | null>(null);
  const [error, setError] = useState<VivaError | null>(null);
  const [task, setTask] = useState<SyncTask | null>(null);
  const running = useRef(false);

  const reload = useCallback(async () => {
    try {
      setSources(await catalog.listSources());
    } catch (cause) {
      setError(asVivaError(cause));
      setSources((current) => current ?? []);
    }
  }, [catalog]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const run = useCallback(
    async (label: string, operation: (context: { signal: AbortSignal; onProgress: (p: SyncProgress) => void }) => Promise<unknown>) => {
      if (running.current) {
        setError(new VivaError('invalid-input', t('sync.busy')));
        return false;
      }
      running.current = true;
      const controller = new AbortController();
      setError(null);
      setTask({ label, progress: null, cancel: () => controller.abort() });
      let ready = 0;
      try {
        await operation({
          signal: controller.signal,
          onProgress: (progress) => {
            setTask((current) => (current ? { ...current, progress } : current));
            // Live TV became viewable while the rest is still loading.
            if (progress.ready.length > ready) {
              ready = progress.ready.length;
              void reload();
            }
          },
        });
        return true;
      } catch (cause) {
        const failure = asVivaError(cause);
        if (failure.code !== 'cancelled') setError(failure);
        return false;
      } finally {
        running.current = false;
        setTask(null);
        await reload();
      }
    },
    [reload],
  );

  const value = useMemo<CatalogState>(
    () => ({
      catalog,
      sources,
      error,
      task,
      dismissError: () => setError(null),
      addSource: (input) => run(t('sync.adding', { name: input.name || 'IPTViva' }), (context) => catalog.addSource(input, context)),
      refreshSource: (source) => run(t('sync.updating', { name: source.name }), (context) => catalog.refreshSource(source.id, context)),
      reimportFile: (source, file) => run(t('sync.importing', { name: file.name }), (context) => catalog.reimportFile(source.id, file, context)),
      removeSource: async (source) => {
        try {
          await catalog.removeSource(source.id);
        } catch (cause) {
          setError(asVivaError(cause));
        }
        await reload();
      },
    }),
    [catalog, sources, error, task, run, reload],
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}
